/**
 * A connection teardown (force Reconnect, disconnect) rolls back and releases
 * every transaction session pinned to that connection — on every engine that
 * has them. The renderer used to hear nothing: the toolbar kept reporting an
 * active transaction, the next run skipped BEGIN, and the backend ran it in
 * plain auto-commit (review 2026-09-05, P1). This resets the affected tabs and
 * says so; the backend's `TRANSACTION_SESSION_MISSING` refusal is the backstop.
 *
 * Two races, both closed by the session GENERATION (follow-up review, P1):
 *
 *  - The manager emits AFTER an async teardown, so the event can land after the
 *    same tab already began a NEWER transaction. Each entry names the session
 *    it lost; a tab is reset only while it still holds that generation.
 *  - `QueryEditorTab`'s disconnected-status effect ("Transaction lost") is the
 *    fallback for a network drop the backend never announces. When a teardown
 *    IS announced, the two must not both act: whichever runs first records the
 *    `(tabId, generation, outcome)` it announced, and the other repeats nothing
 *    — but the fallback can only say `unknown`, so a `rolled_back` /
 *    `committed` a release later CONFIRMS still updates the toast (final review).
 */

import { useEffect } from 'react';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { isQueryTab, type QueryTabContent } from '../../core/types/workspaceTypes';
import type { TransactionSessionsReleasedEvent } from '../../../../types/electron';
import { toast } from '../../../../components/ui/toaster';

/**
 * The ONE shape every "this tab's transaction is over" path writes — the
 * release event here, the disconnected-status fallback in `QueryEditorTab`,
 * Commit/Rollback in `useTransactionControl`, and the
 * `TRANSACTION_SESSION_MISSING` refusal in `useQueryExecution`. The fallback
 * used to leave the tab in `error` with its generation intact while the event
 * reset it to `idle` (round-2 review): the tab then offered a Rollback for a
 * session the backend had already released, and a late release event for the
 * same generation reset it a second time.
 */
export const TRANSACTION_TAB_IDLE_RESET: Pick<QueryTabContent, 'transactionState' | 'transactionSessionGeneration'> = {
  transactionState: 'idle',
  transactionSessionGeneration: undefined,
};

/**
 * A COMMIT / ROLLBACK that failed for any reason but `SETTLE_IN_PROGRESS`: the
 * backend has already ended that session (rolled back, or its connection
 * destroyed), so the tab goes idle and keeps the error in its messages
 * (Codex r7). A phantom `active` blocked every later close and session switch.
 */
export function transactionEndedByFailedSettle(
  content: QueryTabContent,
  reason: string,
): Pick<QueryTabContent, 'transactionState' | 'transactionSessionGeneration' | 'messages'> {
  return {
    ...TRANSACTION_TAB_IDLE_RESET,
    messages: [
      ...(content.messages ?? []),
      {
        id: `settle-${Date.now()}`,
        type: 'error',
        content: `${reason.replace(/[.\s]*$/, '.')} The transaction is no longer open.`,
        timestamp: new Date(),
      },
    ],
  };
}

const REASON_LABEL: Record<TransactionSessionsReleasedEvent['reason'], string> = {
  reconnect: 'reconnect',
  disconnect: 'disconnect',
};

type ReleaseOutcome = TransactionSessionsReleasedEvent['tabs'][number]['outcome'];

/**
 * What the disconnected-status fallback can honestly say: nothing is confirmed
 * until a release or the teardown's event reports it (final review, MAJOR — it
 * used to announce "rolled back by the server" on no evidence).
 */
export const TRANSACTION_LOST_DESCRIPTION =
  'The connection dropped; whether the last statements were committed is not known — check the data before repeating them.';

/**
 * Outcomes already announced per `(tabId, generation)`. Keyed by OUTCOME too
 * (final review): the fallback's `unknown` must not suppress the authoritative
 * `rolled_back` / `committed` a release confirms later — that one still
 * updates the toast — while a repeated outcome, or an `unknown` after anything,
 * adds nothing and stays silent.
 */
const announcedOutcomes = new Map<string, Set<ReleaseOutcome>>();
const ANNOUNCED_CAP = 256;

/**
 * ONE key derivation for both reactions, from the generation the TAB holds —
 * never from the event's. The fallback only has the tab's (`tab:none` when
 * `begin` returned no generation), and the event path used to fall back to the
 * event's generation for the same tab (`tab:<n>`), so the two never matched
 * and a legacy tab got both toasts (round-2 review).
 */
const releaseKey = (tabId: string, generation: number | undefined): string => `${tabId}:${generation ?? 'none'}`;

/**
 * May `outcome` be announced for this session? Records it when so. `unknown`
 * is news only while nothing has been said; a confirmed outcome is news unless
 * that same outcome already was.
 */
export function claimTransactionOutcome(tabId: string, generation: number | undefined, outcome: ReleaseOutcome): boolean {
  const key = releaseKey(tabId, generation);
  const seen = announcedOutcomes.get(key);
  if (seen && (seen.has(outcome) || outcome === 'unknown')) return false;
  if (seen) {
    seen.add(outcome);
    return true;
  }
  if (announcedOutcomes.size >= ANNOUNCED_CAP) {
    const oldest = announcedOutcomes.keys().next().value;
    if (oldest !== undefined) announcedOutcomes.delete(oldest);
  }
  announcedOutcomes.set(key, new Set([outcome]));
  return true;
}

/** Test-only. */
export function _resetHandledTransactionReleasesForTest(): void {
  announcedOutcomes.clear();
}

/**
 * Does the release event name the session this tab currently holds? A tab
 * without a recorded generation (a session persisted before the field existed)
 * is treated as matching — resetting it is the safe direction.
 */
export function releaseMatchesTab(content: Pick<QueryTabContent, 'transactionSessionGeneration'>, sessionGeneration: number): boolean {
  return content.transactionSessionGeneration === undefined || content.transactionSessionGeneration === sessionGeneration;
}

/**
 * The disconnected-status fallback in `QueryEditorTab`: should it mark this
 * tab's transaction lost? Not when the tab is not in a transaction, and not
 * when a release event already said anything about this very session. When it
 * does act it records `unknown` — all it can honestly claim — so a confirmed
 * outcome arriving later still gets announced.
 */
export function claimTransactionLostFallback(
  tabId: string,
  content: Pick<QueryTabContent, 'transactionState' | 'transactionSessionGeneration'>,
): boolean {
  if (content.transactionState !== 'active') return false;
  return claimTransactionOutcome(tabId, content.transactionSessionGeneration, 'unknown');
}

/**
 * The fallback's follow-up: its `transaction:release` came back with a
 * CONFIRMED outcome for the session it announced as `unknown`. Toasts the
 * update once; a teardown event reporting the same outcome later stays silent.
 */
export function announceConfirmedTransactionOutcome(
  tabId: string,
  generation: number | undefined,
  outcome: ReleaseOutcome,
  reason: TransactionSessionsReleasedEvent['reason'],
): void {
  if (outcome === 'unknown' || !claimTransactionOutcome(tabId, generation, outcome)) return;
  const title = useWorkspaceStore.getState().tabs.find((t) => t.id === tabId)?.title;
  toastReleaseOutcome(reason, outcome, title ? `in "${title}"` : 'in this tab');
}

function toastReleaseOutcome(reason: TransactionSessionsReleasedEvent['reason'], outcome: ReleaseOutcome, where: string): void {
  toast.warning({ title: `Transaction ended by ${REASON_LABEL[reason]}`, description: releaseOutcomeDescription(outcome, where) });
}

/**
 * Reset every listed query tab that still holds the named session to `idle`
 * and show one toast for the batch. Returns the tab ids that were reset
 * (exported for unit tests).
 */
export function applyTransactionSessionsReleased(event: TransactionSessionsReleasedEvent): string[] {
  const { tabs, updateTabContent } = useWorkspaceStore.getState();
  const reset: string[] = [];
  const toToast: Array<{ tabId: string; outcome: ReleaseOutcome }> = [];
  for (const { tabId, sessionGeneration, outcome } of event.tabs) {
    const tab = tabs.find((t) => t.id === tabId);
    if (!tab || !isQueryTab(tab)) continue;
    let generation: number | undefined;
    if (tab.content.transactionState !== 'idle' && releaseMatchesTab(tab.content, sessionGeneration)) {
      updateTabContent(tabId, TRANSACTION_TAB_IDLE_RESET);
      reset.push(tabId);
      generation = tab.content.transactionSessionGeneration;
    } else {
      // The tab no longer holds this session — the fallback reset it, or a
      // newer transaction began. Only an outcome for a session the fallback
      // announced as `unknown` is still news (final review); anything else is
      // a late event about a session nobody is waiting on.
      generation = sessionGeneration;
      if (!announcedOutcomes.has(releaseKey(tabId, generation))) continue;
    }
    // The fallback effect may already have told the user about this session:
    // repeat nothing, but a CONFIRMED outcome after its `unknown` is an update.
    if (!claimTransactionOutcome(tabId, generation, outcome ?? 'unknown')) continue;
    toToast.push({ tabId, outcome: outcome ?? 'unknown' });
  }
  if (toToast.length === 0) return reset;

  // One toast per OUTCOME, worded for what actually happened: a COMMIT that
  // was already on the wire when the teardown landed is not "rolled back", and
  // a settle the teardown could not see through is not either (round-2 review).
  for (const outcome of ['rolled_back', 'committed', 'unknown'] as const) {
    const ids = toToast.filter((t) => t.outcome === outcome).map((t) => t.tabId);
    if (ids.length === 0) continue;
    const titles = ids.map((id) => tabs.find((t) => t.id === id)?.title).filter((t): t is string => Boolean(t));
    const where = titles.length === 1 ? `in "${titles[0]}"` : `in ${ids.length} tabs`;
    toastReleaseOutcome(event.reason, outcome, where);
  }
  return reset;
}

/** Exported for unit tests. */
export function releaseOutcomeDescription(outcome: ReleaseOutcome, where: string): string {
  switch (outcome) {
    case 'rolled_back':
      return `Uncommitted changes ${where} were rolled back. The next statement starts a new transaction.`;
    case 'committed':
      return `The commit ${where} had already been sent and completed; nothing was rolled back. The next statement starts a new transaction.`;
    case 'unknown':
      return `Whether the changes ${where} were committed or rolled back could not be confirmed — check the data before running them again. The next statement starts a new transaction.`;
  }
}

export function useTransactionSessionsReleased(): void {
  useEffect(() => {
    const cleanup = window.electronAPI?.transaction?.onSessionsReleased?.(applyTransactionSessionsReleased);
    return () => { cleanup?.(); };
  }, []);
}
